import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heart, FileText, Upload, Mic, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { recordsAPI } from '@/lib/api'
import { useNavigate } from 'react-router-dom'

export default function AddRecordPage() {
  const [recordType, setRecordType] = useState<'personal' | 'medical' | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const { toast } = useToast()
  const navigate = useNavigate()

  const handleRecordTypeSelect = (type: 'personal' | 'medical') => {
    setRecordType(type)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const formData = new FormData(e.target as HTMLFormElement)
      
      if (recordType === 'personal') {
        const data = {
          condition: formData.get('condition') as string,
          notes: formData.get('notes') as string,
        }
        
        await recordsAPI.createPersonalLog(data)
        toast({
          title: "Personal health log saved!",
          description: "Your health log has been successfully recorded.",
        })
      } else if (recordType === 'medical') {
        const data = {
          documentType: formData.get('document-type') as string,
          recordDetails: formData.get('record-details') as string,
        }
        
        await recordsAPI.createMedicalRecord(data, selectedFile || undefined)
        toast({
          title: "Medical record saved!",
          description: "Your medical record has been successfully uploaded.",
        })
      }
      
      navigate('/app/records')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to save record"
      toast({
        title: "Save failed",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!recordType) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Add New Record</h1>
          <p className="text-muted-foreground">
            Choose the type of health record you'd like to create
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card 
            className="gradient-card cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleRecordTypeSelect('personal')}
          >
            <CardHeader className="text-center">
              <Heart className="h-16 w-16 text-primary mx-auto mb-4" />
              <CardTitle>Personal Health Log</CardTitle>
              <CardDescription>
                Track your daily health, symptoms, feelings, and personal observations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Record symptoms and feelings</li>
                <li>• Add voice notes and photos</li>
                <li>• AI-powered text extraction</li>
                <li>• Personal health insights</li>
              </ul>
            </CardContent>
          </Card>

          <Card 
            className="gradient-card cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleRecordTypeSelect('medical')}
          >
            <CardHeader className="text-center">
              <FileText className="h-16 w-16 text-primary mx-auto mb-4" />
              <CardTitle>Medical Record</CardTitle>
              <CardDescription>
                Upload and manage official medical documents and test results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Upload medical documents</li>
                <li>• Scan lab results and reports</li>
                <li>• Automatic data extraction</li>
                <li>• Organize by document type</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Add {recordType === 'personal' ? 'Personal Health Log' : 'Medical Record'}
          </h1>
          <p className="text-muted-foreground">
            {recordType === 'personal' 
              ? 'Record your health observations and symptoms'
              : 'Upload and organize your medical documents'
            }
          </p>
        </div>
        <Button variant="outline" onClick={() => setRecordType(null)}>
          Back to Selection
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {recordType === 'personal' ? (
              <Heart className="h-5 w-5 mr-2 text-primary" />
            ) : (
              <FileText className="h-5 w-5 mr-2 text-primary" />
            )}
            {recordType === 'personal' ? 'Personal Health Log' : 'Medical Record'}
          </CardTitle>
          <CardDescription>
            Fill in the details for your new health record
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {recordType === 'personal' ? (
              <>
                <div className="space-y-2">
                  <label htmlFor="condition" className="text-sm font-medium">
                    Condition or Symptom
                  </label>
                  <Input
                    id="condition"
                    name="condition"
                    placeholder="e.g., Headache, Fatigue, General checkup"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="notes" className="text-sm font-medium">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Describe how you're feeling, symptoms, or any observations..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button type="button" variant="outline" className="h-20">
                    <Mic className="h-6 w-6 mr-2" />
                    Record Voice Note
                  </Button>
                  <Button type="button" variant="outline" className="h-20">
                    <Upload className="h-6 w-6 mr-2" />
                    Upload Photo
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <label htmlFor="document-type" className="text-sm font-medium">
                    Document Type
                  </label>
                  <select
                    id="document-type"
                    name="document-type"
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Select document type</option>
                    <option value="lab-result">Lab Result</option>
                    <option value="prescription">Prescription</option>
                    <option value="medical-report">Medical Report</option>
                    <option value="imaging">Medical Imaging</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="record-details" className="text-sm font-medium">
                    Record Details
                  </label>
                  <textarea
                    id="record-details"
                    name="record-details"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Add any additional details about this medical record..."
                  />
                </div>

                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Upload Medical Document</h3>
                  <p className="text-muted-foreground mb-4">
                    {selectedFile ? selectedFile.name : 'Drag and drop your file here, or click to browse'}
                  </p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    onChange={handleFileSelect}
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    Choose File
                  </Button>
                </div>
              </>
            )}

            <div className="flex gap-4">
              <Button type="submit" className="flex-1" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Record'
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => setRecordType(null)} disabled={isLoading}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}